import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FileText, Plus, Trash2, BookOpen } from 'lucide-react';
import { useTranslation } from '../../../contexts/TranslationContext';
import { Lesson, Assessment, AssessmentItem } from '../../../types';
import { CountObjectsCanvasConfigurationForm } from '../ResourceSelectorComponents/BasicResourceConfigurationForm';
import { SlamItemPicker } from './SlamItemPicker';
import { gradeToSlamGrade } from '../../../services/slamService';
import { LaTeXRenderer } from '../../Common/LaTeXRenderer';

interface AssessmentsSectionProps {
  formData: Partial<Lesson>;
  setFormData: React.Dispatch<React.SetStateAction<Partial<Lesson>>>;
}

export const AssessmentsSection: React.FC<AssessmentsSectionProps> = ({
  formData,
  setFormData
}) => {
  const { t } = useTranslation();
  const [slamPickerTarget, setSlamPickerTarget] = useState<string | null>(null);

  const assessmentItemTypes = [
    { value: 'quiz_resource', label: t('assessmentsSection.quizResource') },
    { value: 'count_objects', label: t('assessmentsSection.countObjects') },
    { value: 'ten_frame_read', label: t('assessmentsSection.tenFrameRead') },
    { value: 'number_line_point', label: t('assessmentsSection.numberLinePoint') },
    { value: 'before_after', label: t('assessmentsSection.beforeAfter') },
    { value: 'compose', label: t('assessmentsSection.composeNumbers') },
    { value: 'sequence', label: t('assessmentsSection.sequence') },
    { value: 'pattern', label: t('assessmentsSection.pattern') },
    { value: 'comparison', label: t('assessmentsSection.comparison') },
    { value: 'multiple_choice', label: t('assessmentsSection.multipleChoice') }
  ];
  const addAssessment = () => {
    const assessmentId = `assessment_${Date.now()}`;
    const newAssessment: Assessment = {
      items: [],
      scoring: {
        correct_per_item: 1,
        max: 10,
        mastery_threshold: 8
      }
    };

    setFormData(prev => ({
      ...prev,
      assessments: {
        ...prev.assessments,
        [assessmentId]: newAssessment
      }
    }));
  };

  const removeAssessment = (assessmentId: string) => {
    setFormData(prev => {
      const newAssessments = { ...prev.assessments };
      delete newAssessments[assessmentId];
      return {
        ...prev,
        assessments: newAssessments
      };
    });
  };

  const addAssessmentItem = (assessmentId: string) => {
    const newItem: AssessmentItem = {
      id: `item_${Date.now()}`,
      type: 'quiz_resource',
      prompt: '',
      answer: 0,
      choices: []
    };

    setFormData(prev => ({
      ...prev,
      assessments: {
        ...prev.assessments,
        [assessmentId]: {
          ...prev.assessments![assessmentId],
          items: [...(prev.assessments![assessmentId].items || []), newItem]
        }
      }
    }));
  };

  const removeAssessmentItem = (assessmentId: string, itemIndex: number) => {
    setFormData(prev => ({
      ...prev,
      assessments: {
        ...prev.assessments,
        [assessmentId]: {
          ...prev.assessments![assessmentId],
          items: prev.assessments![assessmentId].items?.filter((_, i) => i !== itemIndex) || []
        }
      }
    }));
  };

  const updateAssessmentItem = (assessmentId: string, itemIndex: number, field: keyof AssessmentItem, value: any) => {
    setFormData(prev => ({
      ...prev,
      assessments: {
        ...prev.assessments,
        [assessmentId]: {
          ...prev.assessments![assessmentId],
          items: prev.assessments![assessmentId].items?.map((item, i) => 
            i === itemIndex ? { ...item, [field]: value } : item
          ) || []
        }
      }
    }));
  };

  // Get available quiz resources
  const getQuizResources = () => {
    return Object.entries(formData.resources || {})
      .filter(([_, resource]) => resource.type === 'quiz')
      .map(([id, resource]) => ({ id, resource }));
  };

  const addAssessmentChoice = (assessmentId: string, itemIndex: number) => {
    setFormData(prev => ({
      ...prev,
      assessments: {
        ...prev.assessments,
        [assessmentId]: {
          ...prev.assessments![assessmentId],
          items: prev.assessments![assessmentId].items?.map((item, i) => 
            i === itemIndex 
              ? { ...item, choices: [...(item.choices || []), ''] }
              : item
          ) || []
        }
      }
    }));
  };

  const updateAssessmentChoice = (assessmentId: string, itemIndex: number, choiceIndex: number, value: string | number) => {
    setFormData(prev => ({
      ...prev,
      assessments: {
        ...prev.assessments,
        [assessmentId]: {
          ...prev.assessments![assessmentId],
          items: prev.assessments![assessmentId].items?.map((item, i) => 
            i === itemIndex 
              ? { 
                  ...item, 
                  choices: item.choices?.map((choice, j) => j === choiceIndex ? value : choice) || []
                }
              : item
          ) || []
        }
      }
    }));
  };

  const removeAssessmentChoice = (assessmentId: string, itemIndex: number, choiceIndex: number) => {
    setFormData(prev => ({
      ...prev,
      assessments: {
        ...prev.assessments,
        [assessmentId]: {
          ...prev.assessments![assessmentId],
          items: prev.assessments![assessmentId].items?.map((item, i) => 
            i === itemIndex 
              ? { 
                  ...item, 
                  choices: item.choices?.filter((_, j) => j !== choiceIndex) || []
                }
              : item
          ) || []
        }
      }
    }));
  };

  const addSlamItems = (assessmentId: string, items: AssessmentItem[]) => {
    setFormData(prev => ({
      ...prev,
      assessments: {
        ...prev.assessments,
        [assessmentId]: {
          ...prev.assessments![assessmentId],
          items: [...(prev.assessments![assessmentId].items || []), ...items]
        }
      }
    }));
    setSlamPickerTarget(null);
  };

  const updateAssessmentScoring = (assessmentId: string, field: string, value: number) => {
    setFormData(prev => {
      const currentAssessment = prev.assessments?.[assessmentId];
      if (!currentAssessment) return prev;
      const maxPoints = prev.assessments?.[assessmentId].items?.reduce((sum, item) => sum + (item.points || 0), 0) || 10;
      const currentScoring = currentAssessment.scoring || {
        correct_per_item: 1,
        max: maxPoints,
        mastery_threshold: Math.floor(maxPoints * 0.8)
      };

      const updatedScoring = {
        ...currentScoring,
        [field]: value
      };

      return {
        ...prev,
        assessments: {
          ...prev.assessments,
          [assessmentId]: {
            ...currentAssessment,
            scoring: updatedScoring
          }
        }
      };
    });
  };

  return (
    <div className="card">
      <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
        <FileText className="w-5 h-5 mr-2" />
        {t('assessmentsSection.assessments')}
      </h2>
      
      <div className="space-y-6">
        {Object.entries(formData.assessments || {}).map(([assessmentId, assessment]) => (
          <motion.div
            key={assessmentId}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="border border-gray-200 rounded-lg p-6 bg-gray-50"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">{t('assessmentsSection.assessments')}: {assessmentId}</h3>
              <button
                type="button"
                onClick={() => removeAssessment(assessmentId)}
                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>

            {/* Assessment Items */}
            <div className="space-y-4 mb-4">
              <h4 className="font-medium text-gray-900">{t('assessmentsSection.assessmentItem')}</h4>
              {assessment.items?.map((item, itemIndex) => (
                <div key={item.id} className="border border-gray-300 rounded-lg p-4 bg-white">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-2">
                      <h5 className="font-medium text-gray-900">{t('assessmentsSection.assessmentItem')} {itemIndex + 1}</h5>
                      {item.slam_item_id && (
                        <span className="flex items-center space-x-1 bg-blue-100 text-blue-700 text-xs px-2 py-0.5 rounded-full">
                          <BookOpen className="w-3 h-3" />
                          <span>{t('assessmentsSection.slamItemBadge', { id: item.slam_item_id })}</span>
                        </span>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => removeAssessmentItem(assessmentId, itemIndex)}
                      className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>

                  {/* SLAM item — full content preview */}
                  {item.slam_item_id && (
                    <div className="mb-3 bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
                      <div className="flex flex-wrap gap-1.5 mb-2">
                        {item.slam_domain && <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full text-xs">{item.slam_domain}</span>}
                        {item.slam_title && <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full text-xs">{item.slam_title}</span>}
                        {item.slam_skill && <span className="bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full text-xs">{item.slam_skill}</span>}
                        {item.slam_grade && <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded-full text-xs">{item.slam_grade}</span>}
                        {item.slam_difficulty && (
                          <span className="bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full text-xs">
                            {t('assessmentsSection.slamDifficulty', { level: item.slam_difficulty })}
                          </span>
                        )}
                      </div>
                      {item.slam_introduction && (
                        <div className="mb-2 text-gray-800">
                          <span className="font-semibold mr-1">
                            {t('slamPicker.introduction')} :
                          </span>
                          <LaTeXRenderer
                            content={item.slam_introduction}
                            className="inline-block"
                          />
                        </div>
                      )}
                      {item.slam_indication && (
                        <div className="mb-2 text-gray-800">
                          <span className="font-semibold mr-1">
                            {t('slamPicker.indication')} :
                          </span>
                          <LaTeXRenderer
                            content={item.slam_indication}
                            className="inline-block"
                          />
                        </div>
                      )}
                      <LaTeXRenderer
                        content={item.slam_description || item.prompt || ''}
                        className="text-gray-700 mt-1"
                      />
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('assessmentsSection.type')}</label>
                      <select
                        value={item.type}
                        onChange={(e) => updateAssessmentItem(assessmentId, itemIndex, 'type', e.target.value)}
                        className="input-field"
                      >
                        {assessmentItemTypes.map(type => (
                          <option key={type.value} value={type.value}>{type.label}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('assessmentsSection.answer')}</label>
                      <input
                        type="text"
                        value={item.answer}
                        onChange={(e) => updateAssessmentItem(assessmentId, itemIndex, 'answer', e.target.value)}
                        className="input-field"
                        placeholder="0"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('assessmentsSection.points')}</label>
                      <input
                        type="number"
                        value={item.points}
                        onChange={(e) => updateAssessmentItem(assessmentId, itemIndex, 'points', parseInt(e.target.value) || 0)}
                        className="input-field"
                        placeholder="0"
                      />
                    </div>
                  </div>

                  {/* Quiz Resource Selection */}
                  {item.type === 'quiz_resource' ? (
                    <div className="mb-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('assessmentsSection.selectQuizResource')}</label>
                      <select
                        value={item.resource_id || ''}
                        onChange={(e) => updateAssessmentItem(assessmentId, itemIndex, 'resource_id', e.target.value)}
                        className="input-field"
                      >
                        <option value="">{t('assessmentsSection.selectQuizResourcePlaceholder')}</option>
                        {getQuizResources().map(({ id, resource }) => (
                          <option key={id} value={id}>
                            {id} ({resource.questions?.length || 0} {t('assessmentsSection.questions').toLowerCase()})
                          </option>
                        ))}
                      </select>
                      {getQuizResources().length === 0 && (
                        <p className="text-sm text-amber-600 mt-1">
                          {t('assessmentsSection.noQuizResourcesAvailable')}
                        </p>
                      )}
                      {item.resource_id && formData.resources?.[item.resource_id] && (
                        <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                          <h5 className="text-sm font-medium text-gray-700 mb-2">{t('assessmentsSection.quizResourceDetails')}</h5>
                          <div className="text-sm text-gray-600 space-y-1">
                                                          <p><strong>{t('assessmentsSection.resourceId')}</strong> {item.resource_id}</p>
                              <p><strong>{t('assessmentsSection.questions')}</strong> {formData.resources[item.resource_id].questions?.length || 0}</p>
                              <p><strong>{t('assessmentsSection.shuffle')}</strong> {formData.resources[item.resource_id].shuffle ? t('assessmentsSection.yes') : t('assessmentsSection.no')}</p>
                            {formData.resources[item.resource_id].questions?.length > 0 && (
                              <div className="mt-2">
                                <p className="font-medium">{t('assessmentsSection.questionExamples')}</p>
                                <ul className="list-disc list-inside ml-2 space-y-1">
                                  {formData.resources[item.resource_id].questions.slice(0, 3).map((q: any, i: number) => (
                                    <li key={i} className="text-xs">
                                      {q.prompt || `${t('assessmentsSection.question')} ${i + 1}`}
                                    </li>
                                  ))}
                                  {formData.resources[item.resource_id].questions.length > 3 && (
                                    <li className="text-xs text-gray-500">
                                      ...{t('assessmentsSection.andMore').replace('{count}', String(formData.resources[item.resource_id].questions.length - 3))}
                                    </li>
                                  )}
                                </ul>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <>
                      <div className="mb-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('assessmentsSection.prompt')}</label>
                        <input
                          type="text"
                          value={item.prompt}
                          onChange={(e) => updateAssessmentItem(assessmentId, itemIndex, 'prompt', e.target.value)}
                          className="input-field"
                          placeholder={t('assessmentsSection.enterQuestionPrompt')}
                        />
                      </div>

                      {/* Before/After Configuration */}
                      {item.type === 'before_after' && (
                        <div className="space-y-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Base Number</label>
                              <input
                                type="number"
                                value={item.stimulus?.base_number || ''}
                                onChange={(e) => updateAssessmentItem(assessmentId, itemIndex, 'stimulus', { 
                                  ...item.stimulus, 
                                  base_number: parseInt(e.target.value) || 0 
                                })}
                                className="input-field"
                                placeholder="5"
                                min="0"
                                max="100"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Question Type</label>
                              <select
                                value={item.stimulus?.question_type || 'before'}
                                onChange={(e) => updateAssessmentItem(assessmentId, itemIndex, 'stimulus', { 
                                  ...item.stimulus, 
                                  question_type: e.target.value 
                                })}
                                className="input-field"
                              >
                                <option value="before">{t('assessmentsSection.whatComesBefore')}</option>
                                <option value="after">{t('assessmentsSection.whatComesAfter')}</option>
                                <option value="between">{t('assessmentsSection.whatComesBetween')}</option>
                              </select>
                            </div>
                          </div>
                          {item.stimulus?.question_type === 'between' && (
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">{t('assessmentsSection.secondNumberForBetween')}</label>
                              <input
                                type="number"
                                value={item.stimulus?.second_number || ''}
                                onChange={(e) => updateAssessmentItem(assessmentId, itemIndex, 'stimulus', { 
                                  ...item.stimulus, 
                                  second_number: parseInt(e.target.value) || 0 
                                })}
                                className="input-field"
                                placeholder="7"
                                min="0"
                                max="100"
                              />
                            </div>
                          )}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">{t('assessmentsSection.choices')}</label>
                            <div className="space-y-2">
                              {item.choices?.map((choice, choiceIndex) => (
                                <div key={choiceIndex} className="flex items-center space-x-2">
                                  <input
                                    type="number"
                                    value={choice}
                                    onChange={(e) => updateAssessmentChoice(assessmentId, itemIndex, choiceIndex, parseInt(e.target.value) || 0)}
                                    className="input-field flex-1"
                                    placeholder={`${t('assessmentsSection.choice')} ${choiceIndex + 1}`}
                                  />
                                  <button
                                    type="button"
                                    onClick={() => removeAssessmentChoice(assessmentId, itemIndex, choiceIndex)}
                                    className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors"
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </button>
                                </div>
                              ))}
                              <button
                                type="button"
                                onClick={() => addAssessmentChoice(assessmentId, itemIndex)}
                                className="btn-secondary flex items-center space-x-2"
                              >
                                <Plus className="w-3 h-3" />
                                <span>{t('assessmentsSection.addChoice')}</span>
                              </button>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Count Objects Configuration */}
                      {item.type === 'count_objects' && (
                        <div className="space-y-4">
                          <CountObjectsCanvasConfigurationForm resource={{
                            ...item.stimulus, 
                            type: 'count_objects_canvas',
                            objects: Array.isArray(item.stimulus?.objects) ? item.stimulus?.objects : [{
                              type: 'object',
                              count: item.stimulus?.objects || 8,
                              color: 'blue'
                            }],
                          }} 
                          onUpdate={(updates) => updateAssessmentItem(assessmentId, itemIndex, 'stimulus', {
                            ...item.stimulus,
                            ...updates
                          })} />
                          {/* <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">{t('assessmentsSection.objectCount')}</label>
                              <input
                                type="number"
                                value={item.stimulus?.objects || ''}
                                onChange={(e) => updateAssessmentItem(assessmentId, itemIndex, 'stimulus', { 
                                  ...item.stimulus, 
                                  objects: parseInt(e.target.value) || 8 
                                })}
                                className="input-field"
                                placeholder="8"
                                min="1"
                                max="100"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">{t('assessmentsSection.layout')}</label>
                              <select
                                value={item.stimulus?.layout || 'random_clustered'}
                                onChange={(e) => updateAssessmentItem(assessmentId, itemIndex, 'stimulus', { 
                                  ...item.stimulus, 
                                  layout: e.target.value 
                                })}
                                className="input-field"
                              >
                                <option value="random_clustered">Random Clustered</option>
                                <option value="two_groups">Two Groups</option>
                                <option value="linear">Linear</option>
                              </select>
                            </div>
                          </div> */}
                          {/* Choices for Count Objects */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">{t('assessmentsSection.answerChoices')}</label>
                            <div className="space-y-2">
                              {item.choices?.map((choice, choiceIndex) => (
                                <div key={choiceIndex} className="flex items-center space-x-2">
                                  <input
                                    type="number"
                                    value={choice}
                                    onChange={(e) => updateAssessmentChoice(assessmentId, itemIndex, choiceIndex, parseInt(e.target.value) || 0)}
                                    className="input-field flex-1"
                                    placeholder={`Choice ${choiceIndex + 1}`}
                                    min="0"
                                    max="50"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => removeAssessmentChoice(assessmentId, itemIndex, choiceIndex)}
                                    className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors"
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </button>
                                </div>
                              ))}
                              <button
                                type="button"
                                onClick={() => addAssessmentChoice(assessmentId, itemIndex)}
                                className="btn-secondary flex items-center space-x-2"
                              >
                                <Plus className="w-3 h-3" />
                                <span>{t('assessmentsSection.addChoice')}</span>
                              </button>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Ten Frame Configuration */}
                      {item.type === 'ten_frame_read' && (
                        <div className="space-y-4">
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">{t('assessmentsSection.frameValue')}</label>
                              <input
                                type="number"
                                value={item.stimulus?.ten_frame || item.stimulus?.frame_value || ''}
                                onChange={(e) => updateAssessmentItem(assessmentId, itemIndex, 'stimulus', { 
                                  ...item.stimulus, 
                                  ten_frame: parseInt(e.target.value) || 12,
                                  frame_value: parseInt(e.target.value) || 12
                                })}
                                className="input-field"
                                placeholder="12"
                                min="0"
                                max="20"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">{t('assessmentsSection.rows')}</label>
                              <input
                                type="number"
                                value={item.stimulus?.rows || ''}
                                onChange={(e) => updateAssessmentItem(assessmentId, itemIndex, 'stimulus', { 
                                  ...item.stimulus, 
                                  rows: parseInt(e.target.value) || 2
                                })}
                                className="input-field"
                                placeholder="2"
                                min="1"
                                max="4"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">{t('assessmentsSection.columns')}</label>
                              <input
                                type="number"
                                value={item.stimulus?.cols || ''}
                                onChange={(e) => updateAssessmentItem(assessmentId, itemIndex, 'stimulus', { 
                                  ...item.stimulus, 
                                  cols: parseInt(e.target.value) || 5
                                })}
                                className="input-field"
                                placeholder="5"
                                min="1"
                                max="10"
                              />
                            </div>
                          </div>
                          
                          {/* Choices for Ten Frame */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">{t('assessmentsSection.answerChoices')}</label>
                            <div className="space-y-2">
                              {item.choices?.map((choice, choiceIndex) => (
                                <div key={choiceIndex} className="flex items-center space-x-2">
                                  <input
                                    type="number"
                                    value={choice}
                                    onChange={(e) => updateAssessmentChoice(assessmentId, itemIndex, choiceIndex, parseInt(e.target.value) || 0)}
                                    className="input-field flex-1"
                                    placeholder={`Choice ${choiceIndex + 1}`}
                                    min="0"
                                    max="50"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => removeAssessmentChoice(assessmentId, itemIndex, choiceIndex)}
                                    className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors"
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </button>
                                </div>
                              ))}
                              <button
                                type="button"
                                onClick={() => addAssessmentChoice(assessmentId, itemIndex)}
                                className="btn-secondary flex items-center space-x-2"
                              >
                                <Plus className="w-3 h-3" />
                                <span>{t('assessmentsSection.addChoice')}</span>
                              </button>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Number Line Configuration */}
                      {item.type === 'number_line_point' && (
                        <div className="space-y-4">
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">{t('assessmentsSection.numberLineRange')}</label>
                              <input
                                type="text"
                                value={item.stimulus?.number_line || ''}
                                onChange={(e) => updateAssessmentItem(assessmentId, itemIndex, 'stimulus', { 
                                  ...item.stimulus, 
                                  number_line: e.target.value 
                                })}
                                className="input-field"
                                placeholder="0-20"
                              />
                              <p className="text-xs text-gray-500 mt-1">Format: start-end (e.g., 0-20, 10-30)</p>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">{t('assessmentsSection.markedPosition')}</label>
                              <input
                                type="number"
                                value={item.stimulus?.mark || ''}
                                onChange={(e) => updateAssessmentItem(assessmentId, itemIndex, 'stimulus', { 
                                  ...item.stimulus, 
                                  mark: parseInt(e.target.value) || 15
                                })}
                                className="input-field"
                                placeholder="15"
                                min="0"
                                max="1000"
                              />
                              <p className="text-xs text-gray-500 mt-1">The number to mark on the number line</p>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">{t('assessmentsSection.tickEvery')}</label>
                              <input
                                type="number"
                                value={item.stimulus?.step || ''}
                                onChange={(e) => updateAssessmentItem(assessmentId, itemIndex, 'stimulus', { 
                                  ...item.stimulus, 
                                  step: parseInt(e.target.value) || undefined
                                })}
                                className="input-field"
                                placeholder="Auto"
                                min="1"
                                max="100"
                              />
                              <p className="text-xs text-gray-500 mt-1">Show every nth number (leave empty for auto)</p>
                            </div>
                          </div>

                          {/* Advanced Number Line Options */}
                          <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                            <h6 className="text-sm font-medium text-gray-700 mb-3">{t('assessmentsSection.advancedOptions')}</h6>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">{t('assessmentsSection.markStyle')}</label>
                                <select
                                  value={item.stimulus?.mark_style || 'circle'}
                                  onChange={(e) => updateAssessmentItem(assessmentId, itemIndex, 'stimulus', { 
                                    ...item.stimulus, 
                                    mark_style: e.target.value 
                                  })}
                                  className="input-field"
                                >
                                  <option value="circle">Circle</option>
                                  <option value="arrow">Arrow</option>
                                  <option value="star">Star</option>
                                  <option value="highlight">Highlight</option>
                                </select>
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">{t('assessmentsSection.colorTheme')}</label>
                                <select
                                  value={item.stimulus?.color_theme || 'purple'}
                                  onChange={(e) => updateAssessmentItem(assessmentId, itemIndex, 'stimulus', { 
                                    ...item.stimulus, 
                                    color_theme: e.target.value 
                                  })}
                                  className="input-field"
                                >
                                  <option value="purple">Purple</option>
                                  <option value="blue">Blue</option>
                                  <option value="green">Green</option>
                                  <option value="orange">Orange</option>
                                  <option value="red">Red</option>
                                </select>
                              </div>
                            </div>
                            <div className="mt-4">
                              <label className="flex items-center space-x-2">
                                <input
                                  type="checkbox"
                                  checked={item.stimulus?.show_range_labels !== false}
                                  onChange={(e) => updateAssessmentItem(assessmentId, itemIndex, 'stimulus', { 
                                    ...item.stimulus, 
                                    show_range_labels: e.target.checked
                                  })}
                                  className="rounded border-gray-300"
                                />
                                <span className="text-sm text-gray-700">{t('assessmentsSection.showRangeLabels')}</span>
                              </label>
                            </div>
                            <div className="mt-2">
                              <label className="flex items-center space-x-2">
                                <input
                                  type="checkbox"
                                  checked={item.stimulus?.show_step_info !== false}
                                  onChange={(e) => updateAssessmentItem(assessmentId, itemIndex, 'stimulus', { 
                                    ...item.stimulus, 
                                    show_step_info: e.target.checked
                                  })}
                                  className="rounded border-gray-300"
                                />
                                <span className="text-sm text-gray-700">{t('assessmentsSection.showStepInfo')}</span>
                              </label>
                            </div>
                          </div>
                          
                          {/* Choices for Number Line */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">{t('assessmentsSection.answerChoices')}</label>
                            <div className="space-y-2">
                              {item.choices?.map((choice, choiceIndex) => (
                                <div key={choiceIndex} className="flex items-center space-x-2">
                                  <input
                                    type="number"
                                    value={choice}
                                    onChange={(e) => updateAssessmentChoice(assessmentId, itemIndex, choiceIndex, parseInt(e.target.value) || 0)}
                                    className="input-field flex-1"
                                    placeholder={`${t('assessmentsSection.choice')} ${choiceIndex + 1}`}
                                    min="0"
                                    max="100"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => removeAssessmentChoice(assessmentId, itemIndex, choiceIndex)}
                                    className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors"
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </button>
                                </div>
                              ))}
                              <button
                                type="button"
                                onClick={() => addAssessmentChoice(assessmentId, itemIndex)}
                                className="btn-secondary flex items-center space-x-2"
                              >
                                <Plus className="w-3 h-3" />
                                <span>{t('assessmentsSection.addChoice')}</span>
                              </button>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Choices for Compose Numbers */}
                      {item.type === 'compose' && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">{t('assessmentsSection.answerChoices')}</label>
                          <div className="space-y-2">
                            {item.choices?.map((choice, choiceIndex) => (
                              <div key={choiceIndex} className="flex items-center space-x-2">
                                <input
                                  type="number"
                                  value={choice}
                                  onChange={(e) => updateAssessmentChoice(assessmentId, itemIndex, choiceIndex, parseInt(e.target.value) || 0)}
                                  className="input-field flex-1"
                                  placeholder={`${t('assessmentsSection.choice')} ${choiceIndex + 1}`}
                                  min="0"
                                  max="100"
                                />
                                <button
                                  type="button"
                                  onClick={() => removeAssessmentChoice(assessmentId, itemIndex, choiceIndex)}
                                  className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors"
                                >
                                  <Trash2 className="w-3 h-3" />
                                </button>
                              </div>
                            ))}
                            <button
                              type="button"
                              onClick={() => addAssessmentChoice(assessmentId, itemIndex)}
                              className="btn-secondary flex items-center space-x-2"
                            >
                              <Plus className="w-3 h-3" />
                              <span>{t('assessmentsSection.addChoice')}</span>
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Choices for Sequence */}
                      {item.type === 'sequence' && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">{t('assessmentsSection.answerChoices')}</label>
                          <div className="space-y-2">
                            {item.choices?.map((choice, choiceIndex) => (
                              <div key={choiceIndex} className="flex items-center space-x-2">
                                <input
                                  type="number"
                                  value={choice}
                                  onChange={(e) => updateAssessmentChoice(assessmentId, itemIndex, choiceIndex, parseInt(e.target.value) || 0)}
                                  className="input-field flex-1"
                                  placeholder={`${t('assessmentsSection.choice')} ${choiceIndex + 1}`}
                                  min="0"
                                  max="100"
                                />
                                <button
                                  type="button"
                                  onClick={() => removeAssessmentChoice(assessmentId, itemIndex, choiceIndex)}
                                  className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors"
                                >
                                  <Trash2 className="w-3 h-3" />
                                </button>
                              </div>
                            ))}
                            <button
                              type="button"
                              onClick={() => addAssessmentChoice(assessmentId, itemIndex)}
                              className="btn-secondary flex items-center space-x-2"
                            >
                              <Plus className="w-3 h-3" />
                              <span>{t('assessmentsSection.addChoice')}</span>
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Choices for Pattern */}
                      {item.type === 'pattern' && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">{t('assessmentsSection.answerChoices')}</label>
                          <div className="space-y-2">
                            {item.choices?.map((choice, choiceIndex) => (
                              <div key={choiceIndex} className="flex items-center space-x-2">
                                <input
                                  type="number"
                                  value={choice}
                                  onChange={(e) => updateAssessmentChoice(assessmentId, itemIndex, choiceIndex, parseInt(e.target.value) || 0)}
                                  className="input-field flex-1"
                                  placeholder={`${t('assessmentsSection.choice')} ${choiceIndex + 1}`}
                                  min="0"
                                  max="100"
                                />
                                <button
                                  type="button"
                                  onClick={() => removeAssessmentChoice(assessmentId, itemIndex, choiceIndex)}
                                  className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors"
                                >
                                  <Trash2 className="w-3 h-3" />
                                </button>
                              </div>
                            ))}
                            <button
                              type="button"
                              onClick={() => addAssessmentChoice(assessmentId, itemIndex)}
                              className="btn-secondary flex items-center space-x-2"
                            >
                              <Plus className="w-3 h-3" />
                              <span>{t('assessmentsSection.addChoice')}</span>
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Comparison Configuration */}
                      {item.type === 'comparison' && (
                        <div className="space-y-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">{t('assessmentsSection.firstNumber')}</label>
                              <input
                                type="number"
                                value={item.stimulus?.number1 || ''}
                                onChange={(e) => updateAssessmentItem(assessmentId, itemIndex, 'stimulus', { 
                                  ...item.stimulus, 
                                  number1: parseInt(e.target.value) || 7 
                                })}
                                className="input-field"
                                placeholder="7"
                                min="0"
                                max="100"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">{t('assessmentsSection.secondNumber')}</label>
                              <input
                                type="number"
                                value={item.stimulus?.number2 || ''}
                                onChange={(e) => updateAssessmentItem(assessmentId, itemIndex, 'stimulus', { 
                                  ...item.stimulus, 
                                  number2: parseInt(e.target.value) || 12 
                                })}
                                className="input-field"
                                placeholder="12"
                                min="0"
                                max="100"
                              />
                            </div>
                          </div>
                          
                          {/* Choices for Comparison */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">{t('assessmentsSection.answerChoices')}</label>
                            <div className="space-y-2">
                              {item.choices?.map((choice, choiceIndex) => (
                                <div key={choiceIndex} className="flex items-center space-x-2">
                                  <input
                                    type="text"
                                    value={choice}
                                    onChange={(e) => updateAssessmentChoice(assessmentId, itemIndex, choiceIndex, e.target.value)}
                                    className="input-field flex-1"
                                    placeholder={`${t('assessmentsSection.choice')} ${choiceIndex + 1}`}
                                  />
                                  <button
                                    type="button"
                                    onClick={() => removeAssessmentChoice(assessmentId, itemIndex, choiceIndex)}
                                    className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors"
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </button>
                                </div>
                              ))}
                              <button
                                type="button"
                                onClick={() => addAssessmentChoice(assessmentId, itemIndex)}
                                className="btn-secondary flex items-center space-x-2"
                              >
                                <Plus className="w-3 h-3" />
                                <span>{t('assessmentsSection.addChoice')}</span>
                              </button>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Choices for multiple choice */}
                      {item.type === 'multiple_choice' && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">{t('assessmentsSection.answerChoices')}</label>
                          <div className="space-y-2">
                            {item.choices?.map((choice, choiceIndex) => (
                              <div key={choiceIndex} className="flex items-center space-x-2">
                                <input
                                  type="text"
                                  value={choice}
                                  onChange={(e) => updateAssessmentChoice(assessmentId, itemIndex, choiceIndex, e.target.value)}
                                  className="input-field flex-1"
                                  placeholder={`Choice ${choiceIndex + 1}`}
                                />
                                <button
                                  type="button"
                                  onClick={() => removeAssessmentChoice(assessmentId, itemIndex, choiceIndex)}
                                  className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors"
                                >
                                  <Trash2 className="w-3 h-3" />
                                </button>
                              </div>
                            ))}
                            <button
                              type="button"
                              onClick={() => addAssessmentChoice(assessmentId, itemIndex)}
                              className="btn-secondary flex items-center space-x-2"
                            >
                              <Plus className="w-3 h-3" />
                              <span>{t('assessmentsSection.addChoice')}</span>
                            </button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              ))}

              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => addAssessmentItem(assessmentId)}
                  className="btn-secondary flex items-center space-x-2"
                >
                  <Plus className="w-4 h-4" />
                  <span>{t('assessmentsSection.addItem')}</span>
                </button>
                <button
                  type="button"
                  onClick={() => setSlamPickerTarget(assessmentId)}
                  className="flex items-center space-x-2 px-3 py-2 bg-blue-50 text-blue-700 border border-blue-200 rounded-lg text-sm font-medium hover:bg-blue-100 transition-colors"
                >
                  <BookOpen className="w-4 h-4" />
                  <span>{t('assessmentsSection.addFromSlam')}</span>
                </button>
              </div>
            </div>

            {/* Scoring Configuration */}
            <div className="border-t border-gray-200 pt-4">
              <h4 className="font-medium text-gray-900 mb-3">{t('assessmentsSection.scoring')}</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('assessmentsSection.pointsPerCorrect')}</label>
                  <input
                    type="number"
                    value={assessment.scoring?.correct_per_item || 1}
                    onChange={(e) => updateAssessmentScoring(assessmentId, 'correct_per_item', parseInt(e.target.value) || 1)}
                    className="input-field"
                    min="1"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('assessmentsSection.maxScore')}</label>
                  <input
                    type="number"
                    value={assessment.scoring?.max || 10}
                    onChange={(e) => updateAssessmentScoring(assessmentId, 'max', parseInt(e.target.value) || 10)}
                    className="input-field"
                    min="1"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('assessmentsSection.masteryThreshold')}</label>
                  <input
                    type="number"
                    value={assessment.scoring?.mastery_threshold || 8}
                    onChange={(e) => updateAssessmentScoring(assessmentId, 'mastery_threshold', parseInt(e.target.value) || 8)}
                    className="input-field"
                    min="1"
                  />
                </div>
              </div>
            </div>
          </motion.div>
        ))}

        <button
          type="button"
          onClick={addAssessment}
          className="btn-secondary flex items-center space-x-2"
        >
          <Plus className="w-4 h-4" />
          <span>{t('assessmentsSection.addAssessment')}</span>
        </button>
      </div>

      {/* SLAM Item Picker modal */}
      <AnimatePresence>
        {slamPickerTarget && (
          <SlamItemPicker
            defaultGrade={gradeToSlamGrade(formData.metadata?.grade)?.toString()}
            onSelect={(items) => addSlamItems(slamPickerTarget, items)}
            onClose={() => setSlamPickerTarget(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
};
